import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  AlertTriangle, 
  Plus, 
  Eye,
  Clock,
  MapPin,
  Filter,
  Sun,
  Moon,
  Layers
} from "lucide-react";

const incidentTypes = [
  { value: "PhysicalAssault", label: "Physical Assault", color: "bg-red-500/20 text-red-400" },
  { value: "SelfHarm", label: "Self-Harm", color: "bg-orange-500/20 text-orange-400" },
  { value: "SeclusionRestraint", label: "Seclusion/Restraint", color: "bg-amber-500/20 text-amber-400" },
  { value: "MedicationError", label: "Medication Error", color: "bg-purple-500/20 text-purple-400" },
  { value: "Contraband", label: "Contraband", color: "bg-pink-500/20 text-pink-400" },
  { value: "Elopement", label: "Elopement", color: "bg-cyan-500/20 text-cyan-400" },
  { value: "StaffInjury", label: "Staff Injury", color: "bg-blue-500/20 text-blue-400" },
  { value: "Other", label: "Other", color: "bg-zinc-500/20 text-zinc-400" },
];

const units = ["Unit 200", "Unit 300", "Unit 400", "Unit 500", "Unit 600", "Admissions", "Other"];

// Helper to determine shift from time
const getShift = (dateTimeStr) => {
  const date = new Date(dateTimeStr);
  const hour = date.getHours();
  // AM: 7:00 AM (7) to 6:59 PM (18)
  // NOC: 7:00 PM (19) to 6:59 AM (6)
  if (hour >= 7 && hour < 19) {
    return "AM";
  }
  return "NOC";
};

const getShiftBadge = (shift) => {
  if (shift === "AM") {
    return <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 gap-1"><Sun className="w-3 h-3" />AM</Badge>;
  }
  return <Badge variant="outline" className="bg-indigo-500/10 text-indigo-400 border-indigo-500/30 gap-1"><Moon className="w-3 h-3" />NOC</Badge>;
};

export default function Incidents() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterReviewed, setFilterReviewed] = useState("all");
  const [filterShift, setFilterShift] = useState("all");
  const [entryMode, setEntryMode] = useState("single");

  // Single entry form state
  const [formData, setFormData] = useState({
    title: "",
    incident_type: "Other",
    unit: "Unit 200",
    incident_time: new Date().toISOString().slice(0, 16),
  });

  // Bulk entry form state
  const [bulkData, setBulkData] = useState({
    incident_type: "PhysicalAssault",
    unit: "Unit 200",
    shift: "AM",
    date: new Date().toISOString().split('T')[0],
    count: 1,
  });

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      const response = await axios.get(`${API}/incidents`, { withCredentials: true });
      setIncidents(response.data || []);
    } catch (error) {
      console.error("Error fetching incidents:", error);
      toast.error("Failed to load incidents");
    } finally {
      setLoading(false);
    }
  };

  const createIncident = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/incidents`, formData, { withCredentials: true });
      toast.success("Incident logged");
      setShowAddDialog(false);
      setFormData({
        title: "",
        incident_type: "Other",
        unit: "Unit 200",
        incident_time: new Date().toISOString().slice(0, 16),
      });
      fetchIncidents();
    } catch (error) {
      toast.error("Failed to log incident");
    }
  };

  const createBulkIncidents = async (e) => {
    e.preventDefault();
    try {
      const count = parseInt(bulkData.count) || 1;
      const typeLabel = incidentTypes.find(t => t.value === bulkData.incident_type)?.label || bulkData.incident_type;
      
      // Generate timestamps based on shift
      // AM: Random time between 7:00-18:59
      // NOC: Random time between 19:00-23:59 or 0:00-6:59
      const promises = [];
      for (let i = 0; i < count; i++) {
        let hour;
        if (bulkData.shift === "AM") {
          hour = 7 + Math.floor(Math.random() * 12); // 7-18
        } else {
          // NOC: either 19-23 or 0-6
          hour = Math.random() > 0.5 ? (19 + Math.floor(Math.random() * 5)) : Math.floor(Math.random() * 7);
        }
        const minute = Math.floor(Math.random() * 60);
        const incidentTime = `${bulkData.date}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        
        promises.push(
          axios.post(`${API}/incidents`, {
            title: typeLabel,
            incident_type: bulkData.incident_type,
            unit: bulkData.unit,
            incident_time: incidentTime,
          }, { withCredentials: true })
        );
      }
      
      await Promise.all(promises);
      toast.success(`${count} incident${count > 1 ? 's' : ''} logged`);
      setShowAddDialog(false);
      setBulkData({
        incident_type: "PhysicalAssault",
        unit: "Unit 200",
        shift: "AM",
        date: new Date().toISOString().split('T')[0],
        count: 1,
      });
      fetchIncidents();
    } catch (error) {
      toast.error("Failed to log incidents");
    }
  };

  const markReviewed = async (incident) => {
    try {
      await axios.patch(`${API}/incidents/${incident.incident_id}`, {
        reviewed: !incident.reviewed
      }, { withCredentials: true });
      fetchIncidents();
    } catch (error) {
      toast.error("Failed to update incident");
    }
  };

  const getTypeConfig = (type) => incidentTypes.find(t => t.value === type) || incidentTypes[7];

  const filteredIncidents = incidents.filter(inc => {
    if (filterType !== "all" && inc.incident_type !== filterType) return false;
    if (filterReviewed === "reviewed" && !inc.reviewed) return false;
    if (filterReviewed === "unreviewed" && inc.reviewed) return false;
    if (filterShift !== "all" && getShift(inc.incident_time) !== filterShift) return false;
    return true;
  });

  // Group by reviewed status for two-pass review
  const unreviewedIncidents = filteredIncidents.filter(i => !i.reviewed);
  const reviewedIncidents = filteredIncidents.filter(i => i.reviewed);

  // Shift summary counts
  const amCount = incidents.filter(i => getShift(i.incident_time) === "AM").length;
  const nocCount = incidents.filter(i => getShift(i.incident_time) === "NOC").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="incidents-page" className="space-y-6 animate-fade-in">
      {/* Header — stacks vertically on phone, side-by-side from sm+ */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
            Incident Intelligence
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Review, analyze, prepare</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button size="sm" className="h-9" data-testid="add-incident-btn">
              <Plus className="w-4 h-4 mr-2" />
              Log Incident
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Log Incident</DialogTitle>
            </DialogHeader>
            
            <Tabs value={entryMode} onValueChange={setEntryMode}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="single" className="gap-2">
                  <Plus className="w-4 h-4" /> Single Entry
                </TabsTrigger>
                <TabsTrigger value="bulk" className="gap-2" data-testid="bulk-entry-tab">
                  <Layers className="w-4 h-4" /> Bulk Entry
                </TabsTrigger>
              </TabsList>
              
              {/* Single Entry Form */}
              <TabsContent value="single">
                <form onSubmit={createIncident} className="space-y-4">
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-400">
                    For operational tracking only - do not enter patient-identifiable information
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Physical Assault, Elopement Attempt"
                      required
                      data-testid="incident-title"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select 
                        value={formData.incident_type} 
                        onValueChange={(v) => setFormData({ ...formData, incident_type: v })}
                      >
                        <SelectTrigger data-testid="incident-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {incidentTypes.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Select 
                        value={formData.unit} 
                        onValueChange={(v) => setFormData({ ...formData, unit: v })}
                      >
                        <SelectTrigger data-testid="incident-unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {units.map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Date/Time</Label>
                    <Input
                      type="datetime-local"
                      value={formData.incident_time}
                      onChange={(e) => setFormData({ ...formData, incident_time: e.target.value })}
                      data-testid="incident-time"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => setShowAddDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-incident">Log Incident</Button>
                  </div>
                </form>
              </TabsContent>
              
              {/* Bulk Entry Form */}
              <TabsContent value="bulk">
                <form onSubmit={createBulkIncidents} className="space-y-4">
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-400">
                    Quickly log multiple incidents of the same type
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select 
                        value={bulkData.incident_type} 
                        onValueChange={(v) => setBulkData({ ...bulkData, incident_type: v })}
                      >
                        <SelectTrigger data-testid="bulk-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {incidentTypes.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Select 
                        value={bulkData.unit} 
                        onValueChange={(v) => setBulkData({ ...bulkData, unit: v })}
                      >
                        <SelectTrigger data-testid="bulk-unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {units.map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={bulkData.date}
                        onChange={(e) => setBulkData({ ...bulkData, date: e.target.value })}
                        data-testid="bulk-date"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Shift</Label>
                      <Select 
                        value={bulkData.shift} 
                        onValueChange={(v) => setBulkData({ ...bulkData, shift: v })}
                      >
                        <SelectTrigger data-testid="bulk-shift">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AM">
                            <span className="flex items-center gap-2">
                              <Sun className="w-4 h-4 text-amber-400" /> AM (7a-7p)
                            </span>
                          </SelectItem>
                          <SelectItem value="NOC">
                            <span className="flex items-center gap-2">
                              <Moon className="w-4 h-4 text-indigo-400" /> NOC (7p-7a)
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Count</Label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={bulkData.count}
                      onChange={(e) => setBulkData({ ...bulkData, count: e.target.value })}
                      placeholder="Number of incidents"
                      data-testid="bulk-count"
                    />
                  </div>
                  
                  <div className="p-3 bg-zinc-800/50 rounded-lg text-sm">
                    Will log <strong>{bulkData.count}</strong> {incidentTypes.find(t => t.value === bulkData.incident_type)?.label} incident{parseInt(bulkData.count) !== 1 ? 's' : ''} on <strong>{bulkData.unit}</strong> for <strong>{bulkData.shift}</strong> shift on <strong>{bulkData.date}</strong>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => setShowAddDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="submit-bulk">Log {bulkData.count} Incident{parseInt(bulkData.count) !== 1 ? 's' : ''}</Button>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      {/* Shift Summary */}
      <div className="flex gap-4">
        <Card className="bento-card flex-1">
          <CardContent className="pt-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Sun className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{amCount}</p>
                <p className="text-xs text-muted-foreground">AM Shift (7a-7p)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bento-card flex-1">
          <CardContent className="pt-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <Moon className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{nocCount}</p>
                <p className="text-xs text-muted-foreground">NOC Shift (7p-7a)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-48" data-testid="filter-type">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {incidentTypes.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterShift} onValueChange={setFilterShift}>
          <SelectTrigger className="w-40" data-testid="filter-shift">
            <Clock className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Shifts</SelectItem>
            <SelectItem value="AM">
              <span className="flex items-center gap-2">
                <Sun className="w-4 h-4 text-amber-400" /> AM
              </span>
            </SelectItem>
            <SelectItem value="NOC">
              <span className="flex items-center gap-2">
                <Moon className="w-4 h-4 text-indigo-400" /> NOC
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filterReviewed} onValueChange={setFilterReviewed}>
          <SelectTrigger className="w-40" data-testid="filter-reviewed">
            <Eye className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="unreviewed">Unreviewed</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Two-Pass Review Interface */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pass 1: Unreviewed */}
        <Card className="bento-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                Pass 1: Needs Review
              </span>
              <Badge variant="outline">{unreviewedIncidents.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
            {unreviewedIncidents.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                All incidents reviewed!
              </p>
            ) : (
              unreviewedIncidents.map((incident) => {
                const typeConfig = getTypeConfig(incident.incident_type);
                const shift = getShift(incident.incident_time);
                return (
                  <div
                    key={incident.incident_id}
                    data-testid={`incident-${incident.incident_id}`}
                    className={`p-3 rounded-lg border border-zinc-800 bg-zinc-900/50 cursor-pointer
                      hover:border-zinc-700 transition-colors`}
                    onClick={() => setSelectedIncident(incident)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{incident.title}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <MapPin className="w-3 h-3" />
                          {incident.unit}
                          <Clock className="w-3 h-3 ml-2" />
                          {new Date(incident.incident_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 items-end">
                        <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                        {getShiftBadge(shift)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Pass 2: Detail View or Reviewed */}
        <Card className="bento-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">
              {selectedIncident ? "Pass 2: Detail View" : "Reviewed Incidents"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedIncident ? (
              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-lg">{selectedIncident.title}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge className={getTypeConfig(selectedIncident.incident_type).color}>
                      {getTypeConfig(selectedIncident.incident_type).label}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {selectedIncident.unit}
                    </span>
                    {getShiftBadge(getShift(selectedIncident.incident_time))}
                  </div>
                </div>

                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Time</p>
                    <p>{new Date(selectedIncident.incident_time).toLocaleString()}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedIncident.reviewed}
                      onCheckedChange={() => {
                        markReviewed(selectedIncident);
                        setSelectedIncident(null);
                      }}
                      data-testid="mark-reviewed"
                    />
                    <Label>Mark as Reviewed</Label>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setSelectedIncident(null)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {reviewedIncidents.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No reviewed incidents yet
                  </p>
                ) : (
                  reviewedIncidents.map((incident) => {
                    const typeConfig = getTypeConfig(incident.incident_type);
                    const shift = getShift(incident.incident_time);
                    return (
                      <div
                        key={incident.incident_id}
                        className="p-2 rounded-lg border border-zinc-800 bg-zinc-900/30 opacity-60
                          hover:opacity-100 transition-opacity cursor-pointer"
                        onClick={() => setSelectedIncident(incident)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm truncate">{incident.title}</span>
                          <div className="flex items-center gap-1">
                            {getShiftBadge(shift)}
                            <Badge variant="outline" className="text-xs">{typeConfig.label}</Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
